/** /agents — governed AI portfolio and entry point to per-agent workspaces. */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgentSummary } from '@servicesai/api-client';
import {
  EmptyState,
  FilterBar,
  GlassButton,
  GlassCard,
  GlassInput,
  GlassSelect,
  StatusPill,
  useI18n,
} from '@servicesai/ui';
import { admin } from '../lib/api.js';
import { S } from '../strings.js';
import { HeroBand, MetricTile, StatusDot } from '../components/elevated/kit.js';
import { Icon } from '../components/elevated/icons.js';

// White-text chips on aeGov DLS palette colors — all ≥4.5:1 (WCAG 1.4.3).
const ACCENT: Record<string, string> = {
  gold: '#7c5e24', oasis: '#00608d', night: '#3e4046', sky: '#0173ab', sand: '#b2550a', rose: '#b52520',
};

/** Per-staff pinned agents (slugs), persisted locally — see plan point 52. */
const PIN_STORAGE_KEY = 'sai.console.pinnedAgents';

function readPinned(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(PIN_STORAGE_KEY) ?? '[]') as unknown;
    return Array.isArray(parsed) ? parsed.filter((slug): slug is string => typeof slug === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Mini success-rate ring (~44px) — plan point 51. Derived from the card's
 * existing lastRun data: succeeded → 100, failed → 0, no/other data → '—'.
 */
function SuccessRing({ rate, label }: { rate: number | null; label: string }) {
  const size = 44;
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const fraction = rate == null ? 0 : Math.max(0, Math.min(100, rate)) / 100;
  const color = rate == null ? '#9ea2a9' : rate >= 95 ? '#3f8e50' : rate >= 80 ? '#d67907' : '#b52520';
  return (
    <span
      role="img"
      aria-label={`${label}: ${rate == null ? '—' : `${rate}%`}`}
      title={`${label}: ${rate == null ? '—' : `${rate}%`}`}
      className="relative inline-grid shrink-0 place-items-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e6e7ea" strokeWidth={stroke} />
        {rate != null && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${(fraction * circumference).toFixed(2)} ${circumference.toFixed(2)}`}
          />
        )}
      </svg>
      <span className="absolute text-[9px] font-extrabold tabular-nums" style={{ color }} dir="ltr">
        {rate == null ? '—' : `${rate}%`}
      </span>
    </span>
  );
}

/** Portrait with graceful fallback to a colored initials chip. */
function AgentAvatar({ slug, initials, color }: { slug: string; initials: string; color: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span
        className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-sm font-bold text-white shadow-inner ring-1 ring-aeblack-100"
        style={{ backgroundColor: color }}
      >
        {initials}
      </span>
    );
  }
  return (
    <img
      src={`/agent-avatars/${slug}.png`}
      alt=""
      onError={() => setFailed(true)}
      className="h-11 w-11 shrink-0 rounded-full object-cover shadow-sm ring-1 ring-aeblack-100"
    />
  );
}

export function AgentsPage() {
  const { l, lang } = useI18n();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<'all' | AgentSummary['kind']>('all');
  const [health, setHealth] = useState<'all' | 'healthy' | 'attention' | 'not_run'>('all');
  const [pinned, setPinned] = useState<string[]>(readPinned);
  const togglePin = (slug: string) => {
    setPinned((current) => {
      const next = current.includes(slug)
        ? current.filter((candidate) => candidate !== slug)
        : [...current, slug];
      try {
        localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable — pin stays session-local */
      }
      return next;
    });
  };
  const agentsQuery = useQuery({
    queryKey: ['agent-portfolio'],
    queryFn: () => admin().agents(),
  });
  const runMutation = useMutation({
    mutationFn: (slug: string) => admin().runAgent(slug),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['agent-portfolio'] });
    },
  });
  const agents = agentsQuery.data?.agents ?? [];
  const healthOf = (agent: AgentSummary) => {
    if (agent.kind === 'journey') return 'healthy' as const;
    if (!agent.lastRun) return 'not_run' as const;
    return agent.lastRun.status === 'succeeded'
      ? 'healthy' as const
      : agent.lastRun.status === 'failed'
        ? 'attention' as const
        : 'healthy' as const;
  };
  const visible = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    const filtered = agents.filter((agent) => {
      const matchesText =
        !needle ||
        `${agent.slug} ${agent.name.en} ${agent.name.ar} ${agent.title.en} ${agent.title.ar}`
          .toLocaleLowerCase()
          .includes(needle);
      return matchesText && (kind === 'all' || agent.kind === kind) && (health === 'all' || healthOf(agent) === health);
    });
    // Pinned agents sort first (stable within each group) — plan point 52.
    return [...filtered].sort(
      (left, right) => Number(pinned.includes(right.slug)) - Number(pinned.includes(left.slug)),
    );
  }, [agents, health, kind, pinned, search]);

  if (agentsQuery.isLoading) return <EmptyState title={l(S.connecting)} />;
  if (agentsQuery.error) return <EmptyState title={l(S.loadFailed)} hint={(agentsQuery.error as Error).message} />;

  const journeyCount = agents.filter((agent) => agent.kind === 'journey').length;
  const runnable = agents.filter((agent) => agent.kind === 'back_office');
  const enabledCount = agents.filter((agent) => agent.enabled !== false).length;
  const runs24hCount = agents.filter(
    (agent) => agent.lastRun && Date.now() - new Date(agent.lastRun.started_at).getTime() < 24 * 60 * 60 * 1000,
  ).length;
  const healthyCount = agents.filter((agent) => healthOf(agent) === 'healthy').length;
  const attentionCount = agents.filter((agent) => healthOf(agent) === 'attention').length;
  const pinnedCount = agents.filter((agent) => pinned.includes(agent.slug)).length;

  const card = (agent: AgentSummary) => {
    const metric = agent.lastRun?.metric;
    const agentHealth = healthOf(agent);
    const isRunning = runMutation.isPending && runMutation.variables === agent.slug;
    const isPinned = pinned.includes(agent.slug);
    const ringRate = !agent.lastRun
      ? null
      : agent.lastRun.status === 'succeeded'
        ? 100
        : agent.lastRun.status === 'failed'
          ? 0
          : null;
    const healthTone = agent.enabled === false
      ? 'idle' as const
      : agentHealth === 'healthy'
        ? 'live' as const
        : agentHealth === 'attention'
          ? 'down' as const
          : 'idle' as const;
    return (
      <GlassCard
        key={agent.slug}
        className="flex min-h-72 flex-col p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_44px_-22px_rgb(14_15_18/0.4)]"
        strong
      >
        <div className="flex items-start gap-3">
          <AgentAvatar slug={agent.slug} initials={agent.initials} color={ACCENT[agent.accent] ?? '#00608d'} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-base font-bold">
              <StatusDot tone={healthTone} />
              <span className="truncate">{l(agent.name)}</span>
              {isPinned && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-aegold-100 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-aegold-700">
                  <Icon name="star" size={9} className="fill-current" />
                  {l(S.pinnedAgents)}
                </span>
              )}
            </div>
            <div className="truncate text-xs text-gold-deep">{l(agent.title)}</div>
          </div>
          <SuccessRing rate={ringRate} label={l(S.latestRunOutcome)} />
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <button
              type="button"
              aria-pressed={isPinned}
              aria-label={isPinned ? l(S.unpinAgent) : l(S.pinAgent)}
              title={isPinned ? l(S.unpinAgent) : l(S.pinAgent)}
              onClick={(event) => {
                event.stopPropagation();
                togglePin(agent.slug);
              }}
              className="grid h-8 w-8 cursor-pointer place-items-center rounded-lg transition hover:bg-aegold-50"
            >
              <Icon name="star" size={16} className={isPinned ? 'fill-current text-aegold-500' : 'text-aeblack-300'} />
            </button>
            <StatusPill
              state={agentHealth === 'healthy' ? 'published' : agentHealth === 'attention' ? 'rejected' : 'draft'}
              label={agentHealth === 'healthy' ? l(S.agentHealthy) : agentHealth === 'attention' ? l(S.agentAttention) : l(S.neverRun)}
            />
          </div>
        </div>
        <p className="mt-3 min-h-10 text-sm leading-relaxed text-ink-soft">{l(agent.blurb)}</p>
        <dl className="mt-3 grid grid-cols-2 gap-2 border-t border-aeblack-100 pt-3 text-xs">
          <div>
            <dt className="text-ink-faint">{l(S.agentType)}</dt>
            <dd className="mt-0.5 font-semibold">{agent.kind === 'journey' ? l(S.journeyTeam) : l(S.backOfficeTeam)}</dd>
          </div>
          <div>
            <dt className="text-ink-faint">{l(S.lastRun)}</dt>
            <dd className="mt-0.5 font-semibold">
              {agent.lastRun ? new Date(agent.lastRun.started_at).toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-GB') : l(S.neverRun)}
            </dd>
          </div>
          <div>
            <dt className="text-ink-faint">{l(S.configurationVersion)}</dt>
            <dd className="mt-0.5 font-semibold">{agent.config_version ? `v${agent.config_version}` : l(S.codeDefault)}</dd>
          </div>
          <div>
            <dt className="text-ink-faint">{l(S.integrations)}</dt>
            <dd className="mt-0.5 font-semibold">
              {agent.integration_count ?? 0} · {agent.schedule_count ?? 0} {l(S.schedules)}
            </dd>
          </div>
          <div>
            <dt className="text-ink-faint">{l(S.nextSchedule)}</dt>
            <dd className="mt-0.5 font-semibold">
              {agent.next_schedule_at
                ? new Date(agent.next_schedule_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-ink-faint">{l(S.approvals)}</dt>
            <dd className="mt-0.5 font-semibold">{agent.pending_approvals ?? 0}</dd>
          </div>
        </dl>
        <div className="mt-3 min-h-10 rounded-lg bg-aeblack-50 px-3 py-2 text-xs">
          {metric ? (
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-ink-faint">{l(metric.label)}</span>
              <span className="font-mono font-extrabold text-ink">
                {metric.value}{metric.unit ? ` ${l(metric.unit)}` : ''}
              </span>
            </div>
          ) : (
            <span className="text-ink-faint">{agent.kind === 'journey' ? l(S.inEngine) : l(S.agentNoMetric)}</span>
          )}
        </div>
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
          {agent.kind === 'back_office' && (
            <GlassButton
              size="sm"
              disabled={runMutation.isPending}
              onClick={() => runMutation.mutate(agent.slug)}
            >
              {isRunning ? l(S.running) : l(S.runNow)}
            </GlassButton>
          )}
          <Link
            to={`/agents/${encodeURIComponent(agent.slug)}?tab=overview`}
            className="aegov-btn btn-sm inline-flex min-h-10 items-center justify-center"
          >
            {l(S.openWorkspace)}
          </Link>
        </div>
      </GlassCard>
    );
  };

  return (
    <div dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <HeroBand
        eyebrow={l(S.agentPortfolio)}
        title={l(S.agentsTitle)}
        subtitle={l(S.agentsHint)}
        status={
          <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wide text-white/80">
            {agents.length} {l(S.agentRegistered)}
          </span>
        }
        actions={
          <GlassButton onClick={() => void agentsQuery.refetch()} disabled={agentsQuery.isFetching}>
            {agentsQuery.isFetching ? l(S.connecting) : l(S.refresh)}
          </GlassButton>
        }
        metrics={
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricTile dark label={l(S.agentPortfolio)} value={agents.length} hint={`${journeyCount} ${l(S.journeyTeam).toLocaleLowerCase()} · ${runnable.length} ${l(S.backOfficeTeam).toLocaleLowerCase()}`} />
            <MetricTile dark label={l(S.enabled)} value={enabledCount} hint={l(S.agentEnabledHint)} accent="#3f8e50" />
            <MetricTile dark label={l(S.agentRuns24h)} value={runs24hCount} hint={l(S.agentRuns24hHint)} accent="#0173ab" />
            <MetricTile dark label={l(S.agentAttention)} value={attentionCount} hint={`${healthyCount} ${l(S.agentHealthy).toLocaleLowerCase()} · ${l(S.agentFailedRuns)}`} accent={attentionCount ? '#b52520' : '#3f8e50'} />
          </div>
        }
      />

      {runMutation.error && (
        <div className="aegov-alert alert-error mb-4" role="alert">
          <div className="alert-content">{l(S.actionFailed)}: {(runMutation.error as Error).message}</div>
        </div>
      )}
      <FilterBar summary={`${visible.length} ${l(S.agentWorkspaces)}`}>
        <GlassInput
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={l(S.searchAgents)}
          aria-label={l(S.searchAgents)}
          className="w-full sm:w-72"
        />
        <GlassSelect
          value={kind}
          onChange={(event) => setKind(event.target.value as typeof kind)}
          aria-label={l(S.agentType)}
          className="w-full sm:w-52"
        >
          <option value="all">{l(S.allAgentTypes)}</option>
          <option value="journey">{l(S.journeyTeam)}</option>
          <option value="back_office">{l(S.backOfficeTeam)}</option>
        </GlassSelect>
        <GlassSelect
          value={health}
          onChange={(event) => setHealth(event.target.value as typeof health)}
          aria-label={l(S.agentHealth)}
          className="w-full sm:w-52"
        >
          <option value="all">{l(S.allHealthStates)}</option>
          <option value="healthy">{l(S.agentHealthy)}</option>
          <option value="attention">{l(S.agentAttention)}</option>
          <option value="not_run">{l(S.neverRun)}</option>
        </GlassSelect>
      </FilterBar>
      {visible.length > 0 ? (
        <>
          <div className="mb-3 flex items-center gap-2 text-xs font-bold text-ink-soft">
            <Icon
              name="star"
              size={14}
              className={pinnedCount > 0 ? 'fill-current text-aegold-400' : 'text-aeblack-300'}
            />
            <span>{visible.length} {l(S.agentWorkspaces)}</span>
            {pinnedCount > 0 && (
              <span className="font-semibold text-ink-faint">· {pinnedCount} {l(S.pinnedAgents).toLocaleLowerCase()}</span>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3">{visible.map(card)}</div>
        </>
      ) : (
        <GlassCard>
          <EmptyState title={l(S.noAgentsMatch)} hint={l(S.noAgentsMatchHint)} />
        </GlassCard>
      )}
    </div>
  );
}
